'use client';

import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@mui/material';
import Image from 'next/image';

import { Button } from '@/components/atoms';
import OrangeLine from '@/components/atoms/OrangeLine';
import { SectionContainer } from '@/components/layouts/SectionContainer';

/**
 * About Section Component - Organism Design Pattern
 *
 * A two-column layout section displaying company information about Bách Việt.
 * Based on the Figma design with image on the left and content on the right.
 * Features responsive design and follows the project's design system.
 *
 * @param title - Section title
 * @param content - Main content text
 * @param buttonText - Button text
 * @param buttonLink - Button link URL
 * @param image - Background image object
 * @param getImageUrl - Function to get image URL
 * @param getImageAlt - Function to get image alt text
 */
export interface AboutSectionProps {
  title: string;
  content: string;
  buttonText: string;
  buttonLink: string | null;
  image?: {
    id: number;
    url: string;
    alternativeText?: string | null;
  };
}

const AboutSection = ({
  title,
  content,
  buttonText,
  buttonLink,
  image,
}: AboutSectionProps) => {
  return (
    <SectionContainer>
      <Box
        component='section'
        id='about-section'
        data-component='about-section'
        sx={{
          backgroundColor: '#FFFFFF',
          width: '100%',
          minHeight: { xs: 'auto', md: '486px' },
          py: { xs: 6, md: 10 },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          id='about-section-container'
          data-component='about-section-container'
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '495px 1fr' },
            gap: { xs: 4, md: 0 },
            alignItems: 'center',
            minHeight: { xs: 'auto', md: '486px' },
            width: '100%',
          }}
        >
          {/* Left Column - Image */}
          <Box
            id='about-section-image-column'
            data-component='about-section-image-column'
            sx={{
              width: '100%',
              height: { xs: '300px', md: '486px' },
              borderRadius: { xs: '10px', md: '0' },
              overflow: 'hidden',
              order: { xs: 2, md: 1 },
            }}
          >
            {image?.url ? (
              <Box
                id='about-section-image'
                data-component='about-section-image'
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                }}
              >
                <Image
                  src={
                    image.url.startsWith('http')
                      ? image.url
                      : `${process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337'}${image.url}`
                  }
                  alt={image.alternativeText || ''}
                  fill
                  priority={true}
                  sizes='(max-width: 768px) 100vw, 495px'
                  style={{
                    objectFit: 'cover',
                  }}
                />
              </Box>
            ) : (
              // Placeholder when no image is provided
              <Box
                id='about-section-image-placeholder'
                data-component='about-section-image-placeholder'
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#F5F5F5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  fontSize: '16px',
                }}
              >
                Company Image
              </Box>
            )}
          </Box>

          {/* Right Column - Content */}
          <Box
            id='about-section-content-column'
            data-component='about-section-content-column'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: { xs: '0 16px', md: '0 30px' },
              order: { xs: 1, md: 2 },
            }}
          >
            {/* Title */}
            <Typography
              variant='h2'
              component='h2'
              id='about-section-title'
              data-component='about-section-title'
              sx={{
                fontFamily: 'Radio Canada, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '32px', md: '40px' },
                lineHeight: 1.2,
                color: '#001A44',
                mb: 3,
                textTransform: 'uppercase',
              }}
            >
              {title}
            </Typography>

            {/* Orange underline */}
            <Box
              id='about-section-orange-line'
              data-component='about-section-orange-line'
            >
              <OrangeLine width='275px' height='5px' mx='none' mb={3} />
            </Box>

            {/* Content text */}
            <Box
              id='about-section-content'
              data-component='about-section-content'
              sx={{
                fontSize: '18px',
                lineHeight: 1.6,
                color: '#555555',
                mb: 6,
                '& p': {
                  margin: '0 0 16px 0',
                  '&:last-child': {
                    marginBottom: 0,
                  },
                },
              }}
              dangerouslySetInnerHTML={{
                __html: content
                  ? content.replace(
                      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
                      ''
                    )
                  : '',
              }}
            />

            {/* Button */}
            {buttonLink && (
              <Box
                id='about-section-button-container'
                data-component='about-section-button-container'
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Button
                  id='about-section-button'
                  data-component='about-section-button'
                  variant='primary'
                  href={buttonLink}
                  icon={
                    <FontAwesomeIcon
                      icon={faArrowRightLong}
                      style={{ width: '16px', height: '16px' }}
                    />
                  }
                  iconPosition='end'
                  size='large'
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    minWidth: '223px',
                    height: '56px',
                    backgroundColor: 'secondary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                    },
                  }}
                >
                  {buttonText}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default AboutSection;
